import { IntlShape } from 'react-intl';
import isNil from 'lodash/isNil';
import { INTL_LOCALES, LocaleKey } from '@raisin/cp-utils';
import { ProductType } from '../../../../api/dbs/models';
import { DBSProduct, OFFER_PRODUCT_TYPES } from '../../../../types';
import { getInterestRateType } from '../../../../utils/getInterestRateType';
import messages from './messages';

export const getAnnuityPayoutMessageKey = (isAnnuityPayout: boolean) =>
  isAnnuityPayout ? 'interestPayoutOnInterestCredit' : 'interestPayoutAtMaturity';

export const getInterestCreditVisibility = (
  productType: keyof typeof OFFER_PRODUCT_TYPES,
  locale: LocaleKey,
) => !(locale === INTL_LOCALES.NETHERLANDS && productType !== OFFER_PRODUCT_TYPES.OVERNIGHT);

export const getInterestCreditValue = (
  productType: keyof typeof OFFER_PRODUCT_TYPES,
  annuity: DBSProduct['conditions']['annuity'],
  t,
) => {
  const isProductWithAnnuity = annuity.enabled;
  const isTermProductWithOutAnnuity = productType === OFFER_PRODUCT_TYPES.TERM && !annuity.enabled;

  if (isProductWithAnnuity) {
    return t(messages[`interestCreditValue${annuity.frequency?.period}${annuity.frequency?.unit}`]);
  } else if (isTermProductWithOutAnnuity) {
    return t(messages.interestCreditTermWithoutAnnuity);
  }
  return t(messages.none);
};

export const getInterestPayoutValue = (
  productType: keyof typeof OFFER_PRODUCT_TYPES,
  annuity: DBSProduct['conditions']['annuity'],
) =>
  productType === OFFER_PRODUCT_TYPES.OVERNIGHT || productType === OFFER_PRODUCT_TYPES.TERM
    ? messages[getAnnuityPayoutMessageKey(annuity.annuity_payout)]
    : messages.none;

export const getInvestmentAmountRangeValue = (
  minimumAmount: string,
  maximumBalance: string,
  currency: string,
  formatNumber: IntlShape['formatNumber'],
) =>
  Number.isFinite(+minimumAmount) && Number.isFinite(+maximumBalance)
    ? `${formatNumber(+minimumAmount)} - ${formatNumber(+maximumBalance)} ${currency}`
    : '';

const getAvailabilityValue = ({
  contractualTerminationAllowed,
  productType,
  noticePeriod,
}: {
  contractualTerminationAllowed: boolean;
  productType: ProductType;
  noticePeriod?: DBSProduct['conditions']['termination']['notice_period'];
}) => {
  const availabilityMap = {
    [OFFER_PRODUCT_TYPES.TERM]: contractualTerminationAllowed
      ? 'availabilityEarlyTermination'
      : 'availabilityTerm',
    [OFFER_PRODUCT_TYPES.OVERNIGHT]: 'availabilityOvernight',
    [OFFER_PRODUCT_TYPES.NOTICE]: noticePeriod ? `availabilityNotice_${noticePeriod.unit}` : '',
  };
  return availabilityMap[productType] || 'none';
};

export const formatSettlementDayUnits = (
  settlementDay: DBSProduct['conditions']['settlement_day'] | undefined,
  t,
) => {
  if (!settlementDay) return '';
  return settlementDay
    .map((day) => messages?.[`settlementDayUnit${day}`] && t(messages?.[`settlementDayUnit${day}`]))
    .join(', ');
};

export const formatAvailabilityValue = ({
  t,
  messages,
  contractualTerminationAllowed,
  productType,
  noticePeriod,
}) => {
  const availabilityValue = getAvailabilityValue({
    contractualTerminationAllowed,
    productType,
    noticePeriod,
  });
  const message = t(messages[availabilityValue] || 'none');

  if (productType === OFFER_PRODUCT_TYPES.NOTICE) {
    return `${noticePeriod?.period || ''} ${message}`;
  }

  return message;
};

export const formatSettlementValue = ({
  messages,
  settlementDay = [] as DBSProduct['conditions']['settlement_day'],
  formatSettlementDayUnits,
  t,
}) =>
  `${t(messages.availabilityCallalike) || ''} ${formatSettlementDayUnits(settlementDay, t) || ''}`;

export const getRenewalMessage = (renewalConfig: DBSProduct['conditions']['renewal']) => {
  if (!renewalConfig || renewalConfig?.enabled === false) {
    return messages.disabledRenewal;
  }

  if (isNil(renewalConfig.default_product)) {
    return messages.manualRenewal;
  }

  return messages.automaticRenewal;
};

export const getRepresentativeValue = (offer, translationMessages, t, formatNumber) => {
  const formatStyle = {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  const effectiveRate = formatNumber(
    +offer?.interest_rates?.interest_rate_effective / 100,
    formatStyle,
  );

  const nominalRate = formatNumber(
    +offer?.interest_rates?.interest_rate_nominal / 100,
    formatStyle,
  );

  let representativeValue = t(translationMessages.fixedTermRepresentativeValue, {
    effectiveRate,
    nominalRate,
  });

  if (
    offer?.product_type === OFFER_PRODUCT_TYPES.OVERNIGHT ||
    offer?.product_type === OFFER_PRODUCT_TYPES.NOTICE
  ) {
    let period = offer?.conditions?.annuity?.frequency?.period;
    let unit = offer?.conditions?.annuity?.frequency?.unit;

    const frequencies = {
      months: {
        1: t(translationMessages.bankOfferDetailsMonthlyValue),
        3: t(translationMessages.bankOfferDetailsQuarterlyValue),
      },
    };
    representativeValue = t(
      offer?.product_type === OFFER_PRODUCT_TYPES.NOTICE
        ? translationMessages.overnightOrNoticeRepresentativeValue
        : translationMessages.overnightRepresentativeValue,
      {
        effectiveRate,
        nominalRate,
        frequency:
          (frequencies?.[unit]?.[period] && frequencies[unit][period]) ||
          t(translationMessages.bankOfferDetailsClosureMaturityValue),
      },
    );
  }

  return representativeValue;
};

export const getEarlyTerminationRate = (offer: DBSProduct, locale: string) => {
  const rateType = (
    offer.interest_rates?.headline_interest_rate ||
    getInterestRateType(locale) === 'interest_rate_nominal'
      ? 'NOMINAL'
      : 'EFFECTIVE'
  ).toLowerCase();

  if (offer.conditions.termination.interest_policy === 'PRO_RATA') {
    return offer[`interest_rate_${rateType}`];
  }

  return offer.interest_rates[`termination_interest_rate_${rateType}`] || '0.0';
};
